import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { submitConsent } from '../lib/oneTrustApi';
import { ComingSoon } from './ComingSoon';
import { WebformInfoModal } from './WebformInfoModal';
import { OneTrustIcon } from './OneTrustIcon';
import { ImageZoom } from './ImageZoom';

export function DataCollectionManagement() {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const collectionPoints = [
    {
      id: 'hosted-form',
      title: "OneTrust Hosted Form",
      description: "Manage preferences for data collected through hosted forms with optional double opt-in verification for enhanced security and compliance. Double opt-in requires users to confirm their submission via email before it's processed.",
      forms: [
        {
          title: "Standard Form",
          link: "https://fllr-privacy.my.onetrust.com/hosted-webform/consent/73cb21c4-9737-45b0-ba87-528432c7bbca/c5aa7df8-5af1-4158-b68d-4f52e05d8caf",
          description: "Standard data collection form"
        },
        {
          title: "Double Opt-in Form",
          link: "https://fllr-privacy.my.onetrust.com/hosted-webform/consent/73cb21c4-9737-45b0-ba87-528432c7bbca/f4993c81-c433-49db-a251-0b2eb00dec56",
          description: "Enhanced verification with email confirmation"
        }
      ]
    },
    {
      id: 'api',
      title: "External API Integration",
      description: "Manage data collection preferences for API integrations with direct server-to-server communication. Check out our Get in Touch form implementation below for a live example of API integration in action.",
      content: (
        <div className="space-y-8">
          <div className="relative">
            {/* Example implementation section */}
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-xl mb-6">
              <div className="text-center mb-12">
                <h4 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-4">
                  Get Started Today
                </h4>
                <p className="text-xl text-gray-600 dark:text-gray-300">
                  Interested in learning more about OneTrust implementation? Let's connect!
                </p>
              </div>
              
              {/* Non-interactive form preview */}
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl shadow-xl mb-4 pointer-events-none opacity-75">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      disabled
                      className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm dark:bg-gray-700/50 dark:text-white transition-all duration-300"
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      disabled
                      className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm dark:bg-gray-700/50 dark:text-white transition-all duration-300"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      disabled
                      className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm dark:bg-gray-700/50 dark:text-white transition-all duration-300"
                      placeholder="Your company"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                      Message *
                    </label>
                    <textarea
                      disabled
                      rows={4}
                      className="block w-full px-4 py-3 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm dark:bg-gray-700/50 dark:text-white transition-all duration-300"
                      placeholder="Your message"
                    />
                  </div>

                  <button
                    disabled
                    className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-gradient-to-r from-[#5e9bb8] to-[#499da8] opacity-75"
                  >
                    Send Message
                  </button>
                </div>
              </div>

              <Link 
                to="/#contact"
                className="inline-flex items-center text-[#5e9bb8] hover:text-[#499da8] font-medium transition-colors duration-300"
              >
                Click here to try our live Get in Touch form implementation →
              </Link>
            </div>

            {/* API Integration Details */}
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                API Integration Details
              </h4>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Our Get in Touch form demonstrates a complete API integration with OneTrust, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300 mb-4">
                <li>Direct API communication with OneTrust's servers</li>
                <li>Automatic consent receipt generation</li>
                <li>Secure data transmission</li>
                <li>Real-time validation and processing</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'embedded',
      title: "OneTrust Embedded Form",
      description: "Control data collection settings for embedded forms that seamlessly integrate with your website's design",
      comingSoon: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link
            to="/privacy-center"
            className="inline-flex items-center text-[#5e9bb8] hover:text-[#499da8] transition-colors duration-200"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Privacy Center
          </Link>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-12">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-6">
            Data Collection Management
          </h1>
          
          <p className="text-gray-600 dark:text-gray-300 mb-8">
            Control how your data is collected and processed across different interaction points:
          </p>

          <div className="grid gap-8">
            {collectionPoints.map((point) => (
              <div 
                key={point.id}
                className="bg-gray-50 dark:bg-gray-700 p-8 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {point.title}
                  </h3>
                  {point.id === 'hosted-form' && (
                    <button
                      onClick={() => setIsInfoModalOpen(true)}
                      className="relative group"
                      aria-label="Learn more about OneTrust hosted webform"
                    >
                      <div className="absolute -inset-2 bg-green-500 rounded-full opacity-20 info-beacon"></div>
                      <OneTrustIcon size="lg" />
                    </button>
                  )}
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {point.description}
                </p>
                {point.forms ? (
                  <div className="space-y-4">
                    {point.forms.map((form, index) => (
                      <div key={index} className="flex flex-col">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {form.title}
                        </p>
                        <a
                          href={form.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] text-white rounded-xl font-medium transform hover:-translate-y-0.5 transition-all duration-300"
                        >
                          Access Form
                        </a>
                      </div>
                    ))}
                  </div>
                ) : point.content ? (
                  point.content
                ) : point.comingSoon ? (
                  <ComingSoon 
                    title="Coming Soon"
                    description="This feature is currently under development"
                  />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      <WebformInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </div>
  );
}